import { createRoot } from 'react-dom/client';
import cx from 'classnames';
import cn from '../SVG.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
const SVG_CACHE = new Map();
const SVG_CACHED_NOTIFIERS = new Map();

/**
 * Returns whether a supplied SVG exists in the SVG Cache already.
 *
 * @param id the id of the SVG
 */
export const isSVGAvailableInCache = id => !!SVG_CACHE.get(id);

/**
 * Returns the JSX markup for the reference `<use />` tag.
 */
export const getSVG = props => {
  const cachedSVGIcon = isSVGAvailableInCache(props.id) ? getCachedSVGIconAttributes(props.id) : undefined;
  if (cachedSVGIcon !== undefined) {
    SVG_CACHE.set(props.id, cachedSVGIcon);
  }
  const {
    childClassName,
    ...restProps
  } = cachedSVGIcon ?? {};
  return /*#__PURE__*/_jsx("svg", {
    ...restProps,
    children: /*#__PURE__*/_jsx("use", {
      className: cx(childClassName, props.mirroringStyle),
      href: `#${props.id}`
    })
  });
};

/**
 * Return the attributes from a cached SVG.
 */
const getCachedSVGIconAttributes = id => {
  const svgDomRef = document.getElementById(id);
  const svgHasClass = svgDomRef?.parentElement?.classList.contains(cn.icon);
  const svgDomRefChild = svgDomRef?.querySelector('path:not(.svgToolTipPane)');
  return {
    className: svgDomRef?.getAttribute('class') ?? undefined,
    width: svgDomRef?.getAttribute('width') ?? undefined,
    height: svgDomRef?.getAttribute('height') ?? undefined,
    viewBox: svgDomRef?.getAttribute('viewBox') ?? undefined,
    childClassName: svgHasClass ? svgDomRefChild?.getAttribute('class') ?? undefined : ''
  };
};
export const updateSVG = (svgIcon, props, forceRender, ignoreUpdate) => {
  const localNotifiers = SVG_CACHED_NOTIFIERS.get(props.id);
  if (!isSVGAvailableInCache(props.id) && !localNotifiers) {
    addSVGToSVGArea(svgIcon, props, ignoreUpdate).then(() => {
      const svgAttribute = document.getElementById(props.id);
      const cachedSVGIcon = getCachedSVGIconAttributes(props.id);
      SVG_CACHE.set(props.id, cachedSVGIcon);
      if (svgAttribute) {
        SVG_CACHE.set(props.id, cachedSVGIcon);
        SVG_CACHED_NOTIFIERS.get(props.id).forEach(element => {
          element();
        });
        SVG_CACHED_NOTIFIERS.delete(props.id);
      }
    });
  }
  SVG_CACHED_NOTIFIERS.set(props.id, [...(localNotifiers ?? []), forceRender]);
};

/**
 * Adds an SVG to the `sas-svg-area`.
 */
const addSVGToSVGArea = (svgIcon, props, ignoreUpdate) => {
  let svgArea = document.getElementById('sas-svg-area');
  if (svgArea === null) {
    svgArea = createSVGArea();
  }

  // Destructure props so that unsupported attributes are not applied to the <svg /> tag
  const {
    spanStyleTokens,
    spanClassName,
    mirroringStyle,
    ...restProps
  } = props;
  const svgWrapper = document.createElement('span');
  Object.entries(spanStyleTokens).map(([k, v]) => {
    svgWrapper.style.setProperty(k, v);
  });
  svgWrapper.className = `${spanClassName}`;
  // For testing purposes
  svgWrapper.dataset.testid = props.id;
  svgArea.prepend(svgWrapper);
  // If the SVG component has been unmounted already we shouldn't render anything else
  if (ignoreUpdate) {
    return Promise.reject();
  } else {
    return new Promise(function (resolve) {
      if (svgWrapper) {
        const svgAreaRoot = createRoot(svgWrapper);
        const element = svgIcon.render({
          ...restProps,
          ref: () => {
            resolve();
          }
        });
        svgAreaRoot.render(element);
      }
    });
  }
};

/**
 * Creates a unique `sas-svg-area` `div` for SVGs to be added to.
 * Only one `sas-svg-area` should exist (ie. hybrid applications).
 */
const createSVGArea = () => {
  let svgArea = document.getElementById('sas-svg-area');
  if (!svgArea) {
    svgArea = document.createElement('div');
    svgArea.id = 'sas-svg-area';
    svgArea.style.position = 'absolute';
    svgArea.style.overflow = 'hidden';
    svgArea.style.height = '0';
    svgArea.style.width = '0';
    // For testing purposes
    svgArea.dataset.testid = 'sas-svg-area';
    document.body.prepend(svgArea);
  }
  return svgArea;
};
if (document.readyState === 'loading') {
  // Loading hasn't finished yet
  document.addEventListener('DOMContentLoaded', createSVGArea);
} else {
  // `DOMContentLoaded` has already fired
  createSVGArea();
}
//# sourceMappingURL=SVGCache.js.map